import { useEffect, useState } from "react";
import { Plus, Pencil, Trash2, FileDown } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import api, { errMsg } from "@/lib/api";

export default function EmployeesAdmin({ reference }) {
  const [employees, setEmployees] = useState([]);
  const [filter, setFilter] = useState({ q: "", department: "" });
  const [editing, setEditing] = useState(null);
  const [open, setOpen] = useState(false);
  const [exporting, setExporting] = useState(false);

  const load = async () => {
    const params = {};
    if (filter.q) params.q = filter.q;
    if (filter.department) params.department = filter.department;
    const { data } = await api.get("/employees", { params });
    setEmployees(data);
  };
  useEffect(() => { load(); /* eslint-disable-line */ }, [filter.q, filter.department]);

  const openNew = () => { setEditing({ first_name:"", last_name:"", email:"", department: reference?.departments[0]?.name || "", site:"", job_roles: [], supervisor_id:"", notes:"" }); setOpen(true); };
  const openEdit = (e) => { setEditing({ ...e, job_roles: e.job_roles || [] }); setOpen(true); };

  const exportPdf = async () => {
    setExporting(true);
    try {
      const params = {};
      if (filter.department) params.department = filter.department;
      const res = await api.get("/admin/employees/pdf", { params, responseType: "blob" });
      const blob = new Blob([res.data], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      const datePart = new Date().toISOString().slice(0, 10);
      a.download = `pnc-employees-${datePart}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("Contractors PDF downloaded");
    } catch (e) {
      toast.error(errMsg(e, "Failed to export PDF"));
    } finally {
      setExporting(false);
    }
  };

  const save = async () => {
    try {
      const payload = { ...editing };
      Object.keys(payload).forEach((k) => { if (payload[k] === "") delete payload[k]; });
      if (editing.id) {
        await api.patch(`/employees/${editing.id}`, payload);
        toast.success("Contractor updated");
      } else {
        await api.post("/employees", payload);
        toast.success("Contractor added");
      }
      setOpen(false); load();
    } catch (e) { toast.error(errMsg(e, "Failed to save contractor")); }
  };
  const remove = async (id) => {
    if (!window.confirm("Delete this contractor?")) return;
    try { await api.delete(`/employees/${id}`); toast.success("Deleted"); load(); }
    catch (e) { toast.error(errMsg(e, "Failed to delete contractor")); }
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap gap-2 items-center">
        <Input data-testid="emp-search" placeholder="Search by name or email…" value={filter.q} onChange={(e) => setFilter({ ...filter, q: e.target.value })} className="max-w-xs"/>
        <Select value={filter.department || "__all"} onValueChange={(v) => setFilter({ ...filter, department: v === "__all" ? "" : v })}>
          <SelectTrigger data-testid="emp-dept-filter" className="max-w-[200px]"><SelectValue placeholder="All departments"/></SelectTrigger>
          <SelectContent>
            <SelectItem value="__all">All departments</SelectItem>
            {reference?.departments.map((d) => <SelectItem key={d.id} value={d.name}>{d.name}</SelectItem>)}
          </SelectContent>
        </Select>
        <div className="ml-auto text-sm text-muted-foreground">{employees.length} contractors</div>
        <Button data-testid="emp-pdf-btn" variant="outline" disabled={exporting} onClick={exportPdf} className="gap-1.5"><FileDown className="w-4 h-4"/> {exporting ? "Exporting…" : "PDF"}</Button>
        <Button data-testid="emp-new-btn" onClick={openNew} className="gap-1.5"><Plus className="w-4 h-4"/> New</Button>
      </div>

      <div className="border border-border rounded-lg overflow-hidden bg-[#1E1E1E] max-h-[600px] overflow-y-auto">
        <Table>
          <TableHeader className="sticky top-0 bg-[#1E1E1E] z-10">
            <TableRow><TableHead>Name</TableHead><TableHead>Email</TableHead><TableHead>Department</TableHead><TableHead>Site</TableHead><TableHead>Roles</TableHead><TableHead>Supervisor</TableHead><TableHead></TableHead></TableRow>
          </TableHeader>
          <TableBody>
            {employees.map((e) => {
              const sup = reference?.supervisors.find((s) => s.id === e.supervisor_id);
              const roles = e.job_roles || [];
              return (
                <TableRow key={e.id} data-testid={`emp-row-${e.id}`}>
                  <TableCell className="font-medium">{e.first_name} {e.last_name}</TableCell>
                  <TableCell className="text-muted-foreground">{e.email || "—"}</TableCell>
                  <TableCell>{e.department}</TableCell>
                  <TableCell className="text-muted-foreground">{e.site || "—"}</TableCell>
                  <TableCell>
                    <div className="flex flex-wrap gap-1" data-testid={`emp-roles-${e.id}`}>
                      {roles.length === 0 && <span className="text-muted-foreground text-xs">—</span>}
                      {roles.map((r, i) => (
                        <span key={r}
                          className={`text-[11px] px-1.5 py-0.5 rounded ${i === 0 ? "bg-primary/10 text-primary font-medium" : "bg-muted text-muted-foreground"}`}>
                          {r}
                        </span>
                      ))}
                    </div>
                  </TableCell>
                  <TableCell className="text-muted-foreground">{sup?.name || "—"}</TableCell>
                  <TableCell className="text-right whitespace-nowrap">
                    <Button data-testid={`emp-edit-${e.id}`} size="icon" variant="ghost" onClick={() => openEdit(e)}><Pencil className="w-4 h-4"/></Button>
                    <Button data-testid={`emp-del-${e.id}`} size="icon" variant="ghost" onClick={() => remove(e.id)}><Trash2 className="w-4 h-4"/></Button>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg" data-testid="emp-dialog">
          <DialogHeader><DialogTitle>{editing?.id ? "Edit contractor" : "New contractor"}</DialogTitle></DialogHeader>
          {editing && (
            <div className="grid grid-cols-2 gap-3">
              <F label="First name"><Input data-testid="emp-first" value={editing.first_name} onChange={(e) => setEditing({ ...editing, first_name: e.target.value })}/></F>
              <F label="Last name"><Input data-testid="emp-last" value={editing.last_name} onChange={(e) => setEditing({ ...editing, last_name: e.target.value })}/></F>
              <F label="Email" full><Input data-testid="emp-email" type="email" value={editing.email || ""} onChange={(e) => setEditing({ ...editing, email: e.target.value })}/></F>
              <F label="Department">
                <Select value={editing.department} onValueChange={(v) => setEditing({ ...editing, department: v })}>
                  <SelectTrigger data-testid="emp-dept"><SelectValue/></SelectTrigger>
                  <SelectContent>{reference?.departments.map((d) => <SelectItem key={d.id} value={d.name}>{d.name}</SelectItem>)}</SelectContent>
                </Select>
              </F>
              <F label="Site">
                <Select value={editing.site || "__none"} onValueChange={(v) => setEditing({ ...editing, site: v === "__none" ? "" : v })}>
                  <SelectTrigger data-testid="emp-site"><SelectValue placeholder="None"/></SelectTrigger>
                  <SelectContent><SelectItem value="__none">None</SelectItem>{reference?.sites.map((s) => <SelectItem key={s.id} value={s.name}>{s.name}</SelectItem>)}</SelectContent>
                </Select>
              </F>
              <F label="Roles">
                <RolesEditor
                  roles={editing.job_roles || []}
                  options={reference?.job_roles || []}
                  onChange={(roles) => setEditing({ ...editing, job_roles: roles })}
                />
              </F>
              <F label="Supervisor">
                <Select value={editing.supervisor_id || "__none"} onValueChange={(v) => setEditing({ ...editing, supervisor_id: v === "__none" ? "" : v })}>
                  <SelectTrigger data-testid="emp-sup"><SelectValue placeholder="None"/></SelectTrigger>
                  <SelectContent><SelectItem value="__none">None</SelectItem>{reference?.supervisors.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}</SelectContent>
                </Select>
              </F>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button data-testid="emp-save-btn" onClick={save}>Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function F({ label, children, full }) {
  return (
    <div className={`space-y-1.5 ${full ? "col-span-2" : ""}`}>
      <Label className="text-[11px] uppercase tracking-[0.08em] text-muted-foreground font-semibold">{label}</Label>
      {children}
    </div>
  );
}

const ROLE_SLOT_LABELS = [
  { label: "Primary role", required: true },
  { label: "Secondary role", required: false },
  { label: "Third role", required: false },
];

function RolesEditor({ roles, options, onChange }) {
  // Render exactly 3 ordered slots. Empty slots can be left as "none".
  const slots = [0, 1, 2].map((i) => roles[i] || "");
  const updateSlot = (idx, val) => {
    const next = [...slots];
    next[idx] = val === "__none" ? "" : val;
    // De-dup: if value already exists in another slot, blank that other slot.
    if (next[idx]) {
      for (let i = 0; i < next.length; i++) if (i !== idx && next[i] === next[idx]) next[i] = "";
    }
    onChange(next.filter((r) => r));
  };
  return (
    <div className="space-y-2.5" data-testid="roles-editor">
      {slots.map((value, idx) => {
        const { label, required } = ROLE_SLOT_LABELS[idx];
        return (
          <div key={idx} className="space-y-1">
            <Label className="text-[10px] uppercase tracking-[0.08em] text-muted-foreground font-semibold flex items-center gap-1">
              {label}{required && <span className="text-destructive">*</span>}
            </Label>
            <Select value={value || "__none"} onValueChange={(v) => updateSlot(idx, v)}>
              <SelectTrigger data-testid={`emp-role-slot-${idx}`}>
                <SelectValue placeholder={required ? "Choose a primary role" : "(optional)"}/>
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="__none">{required ? "None (no primary role)" : "None"}</SelectItem>
                {options.map((r) => (
                  <SelectItem key={r} value={r} data-testid={`emp-role-opt-${idx}-${r}`}>{r}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        );
      })}
    </div>
  );
}

